/**
 * MED-1383: Net 10 payments already sent to Bill.com.
 *
 * A submitted payment leaves Payments Due for good, so without this list a CM had no
 * way to see that a bill was sent, or what Bill.com did with it. Approving and paying
 * happen in Bill.com; this list only reports the status SGOS last read back.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Stack,
  Group,
  Text,
  Table,
  Badge,
  Center,
  Loader,
  Button,
  Tooltip,
} from '@mantine/core';
import { ExternalLink, Send } from 'react-feather';
import { cohortBillcomApi } from '../../../../services/api/billcom';
import type { BillComPayment } from '../../../../services/api/billcom';
import { formatCents } from '../../../../utils/formatters';
import {
  getPaymentNoteColor,
  getPaymentStatusConfig,
  HISTORY_ROW_STYLE,
  isHistoryPayment,
  STATUS_COLUMN_STYLE,
  WRAPPING_BADGE_STYLES,
} from '../../../../config/compensationConfig';
import { resolveBillcomBillUrl } from '../../../../config/env';

interface Net10SentPaymentsListProps {
  cohortId: string;
  /** Bump to refetch, for example after a submission or a retry. */
  refreshKey?: number;
}

function itemLabel(payment: BillComPayment): string {
  return payment.memo || payment.line_items?.[0]?.memo || 'Event payment';
}

export function Net10SentPaymentsList({
  cohortId,
  refreshKey,
}: Net10SentPaymentsListProps) {
  const [payments, setPayments] = useState<BillComPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  // The spinner shows only on the first load for a cohort. A refresh keeps the
  // current rows on screen instead of swapping the table for a spinner.
  const loadedCohortRef = useRef<string | null>(null);

  const fetchPayments = useCallback(async () => {
    const firstLoad = loadedCohortRef.current !== cohortId;
    try {
      if (firstLoad) setLoading(true);
      setLoadFailed(false);
      const data = await cohortBillcomApi.getNet10SubmittedPayments(cohortId);
      // MED-1383: a voided bill is not owed, and its event is back in Payments Due
      // above. It stays here as history, so this list agrees with the Voided count
      // on Payment Progress. Live rows first, then voided rows, each newest first,
      // whatever order the server sends.
      setPayments(
        [...data].sort(
          (a, b) =>
            Number(isHistoryPayment(a.status)) -
              Number(isHistoryPayment(b.status)) ||
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
      );
      loadedCohortRef.current = cohortId;
    } catch {
      if (firstLoad) setPayments([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments, refreshKey]);

  return (
    <Stack gap="sm" mt="xl" pt="lg" style={{ borderTop: '1px solid #F0EEEA' }}>
      <div>
        <Text fw={700} size="md">
          Sent to Bill.com
        </Text>
        <Text size="sm" c="dimmed">
          Approve and pay these bills in Bill.com. The status updates here.
        </Text>
      </div>

      {loading ? (
        <Center py="lg">
          <Loader size="sm" />
        </Center>
      ) : loadFailed && payments.length === 0 ? (
        <Stack align="center" py="lg" gap="xs">
          <Text size="sm" c="dangerRed" ta="center">
            Could not load the payments sent to Bill.com.
          </Text>
          <Button size="xs" variant="light" onClick={fetchPayments}>
            Try again
          </Button>
        </Stack>
      ) : payments.length > 0 ? (
        <>
          {loadFailed && (
            <Group gap="xs">
              <Text size="xs" c="dangerRed">
                Could not refresh this list. It shows the last loaded statuses.
              </Text>
              <Button
                size="compact-xs"
                variant="subtle"
                onClick={fetchPayments}
              >
                Try again
              </Button>
            </Group>
          )}
          {/* MED-1383: on a narrow screen the table scrolls inside its card. */}
          <Table.ScrollContainer minWidth={720}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Coach</Table.Th>
                  <Table.Th>Item</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Amount</Table.Th>
                  <Table.Th>Invoice</Table.Th>
                  <Table.Th style={STATUS_COLUMN_STYLE}>Status</Table.Th>
                  <Table.Th style={{ textAlign: 'center' }}>Bill.com</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {payments.map(p => {
                  const statusCfg = getPaymentStatusConfig(
                    p.status,
                    p.billcom_scheduled_date
                  );
                  return (
                    <Table.Tr
                      key={p.id}
                      style={isHistoryPayment(p.status) ? HISTORY_ROW_STYLE : undefined}
                    >
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {p.coach_name || 'Unknown'}
                        </Text>
                        {/* The reason Bill.com refused or holds the bill, as in the Flat Rate table.
                            MED-1383: a void is a normal state, so its note is gray, not red. */}
                        {p.error_message && (
                          <Text
                            size="xs"
                            c={getPaymentNoteColor(p.status)}
                            mt={2}
                            style={{ maxWidth: 360 }}
                          >
                            {p.error_message}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{itemLabel(p)}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" fw={600}>
                          {formatCents(p.amount_cents)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {/* MED-1383: an invoice number is one token; keep it on one line. */}
                        <Text
                          size="sm"
                          c={p.invoice_number ? undefined : 'dimmed'}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {p.invoice_number || '-'}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'center' }}>
                        <Badge
                          size="sm"
                          color={statusCfg.color}
                          variant={statusCfg.variant}
                          styles={WRAPPING_BADGE_STYLES}
                        >
                          {statusCfg.label}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'center' }}>
                        {/* MED-1383: a hover tooltip, as on the other icon buttons. */}
                        {p.billcom_bill_id && (
                          <Tooltip label="View in Bill.com">
                            <a
                              href={resolveBillcomBillUrl(p.billcom_bill_id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#2D4739' }}
                              aria-label="View in Bill.com"
                            >
                              <ExternalLink size={14} />
                            </a>
                          </Tooltip>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </>
      ) : (
        <Group justify="center" gap="xs" py="lg">
          <Send size={16} color="#C4C1BC" />
          <Text size="sm" c="dimmed">
            No payments sent to Bill.com yet
          </Text>
        </Group>
      )}
    </Stack>
  );
}
